import {
  mapPrescriptionPrintSettingsToUI,
  PRESCRIPTION_PRINT_DEFAULTS,
  type AppConfigRow,
  type AppointmentProviderRow,
  type PrescriptionPrintLogoSource,
  type PrescriptionPrintSettingsRow,
  type PrescriptionPrintSettingsUI,
} from '@helios/data-access';
import { orgLogoUrl, orgLogoHorizontalUrl } from '../../../shell/shared';
import type { PrintDoctor } from './PrescriptionPrintLayout';

/**
 * Build a scoped print stylesheet. Multiple print surfaces (prescription,
 * invoice, …) can be mounted simultaneously without their `@media print`
 * rules clobbering each other — each stylesheet only activates when the
 * shared `body[data-print-target=<name>]` attribute matches.
 *
 * `triggerPrint(target)` sets that attribute, fires `window.print()`, and
 * clears the attribute when the print dialog closes.
 *
 * Common targets in use: 'prescription' (PRD.184), 'invoice' (PRD.238).
 */
export function makePrintStyles(target: string, mountClass: string): string {
  return `
@media screen {
  .${mountClass} { display: none; }
}
@media print {
  body[data-print-target="${target}"] {
    background: #ffffff !important;
    width: 210mm !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  body[data-print-target="${target}"] * { visibility: hidden !important; }
  body[data-print-target="${target}"] .${mountClass},
  body[data-print-target="${target}"] .${mountClass} * {
    visibility: visible !important;
  }
  body[data-print-target="${target}"] .${mountClass} {
    display: block !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 210mm !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  body[data-print-target="${target}"] .${mountClass} > * {
    width: 210mm !important;
    min-height: 297mm !important;
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
  }
}
`;
}

// Global @page rule (size: A4; margin: 0) is injected once so we don't repeat
// it in every per-target stylesheet. Browsers merge multiple @page rules but
// keeping it single avoids spec edge cases.
export const PAGE_STYLES = `@media print { @page { size: A4; margin: 0; } }`;

/**
 * Trigger a print run scoped to one mount class. The body attribute is set
 * synchronously, `window.print()` is invoked, and `afterprint` (or a fallback
 * timer) restores the attribute so the page returns to its normal screen
 * state. Multiple print surfaces on the same page won't fight: only the
 * stylesheet whose `data-print-target` matches activates its rules.
 */
export function triggerPrint(target: string): void {
  if (typeof document === 'undefined') return;
  const prev = document.body.dataset.printTarget;
  document.body.dataset.printTarget = target;

  const cleanup = () => {
    if (document.body.dataset.printTarget === target) {
      if (prev === undefined) {
        delete document.body.dataset.printTarget;
      } else {
        document.body.dataset.printTarget = prev;
      }
    }
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  // Safari + some embedded webviews don't reliably fire `afterprint`; queue
  // a short fallback so the screen never gets stuck in print-only mode.
  setTimeout(cleanup, 2000);

  window.print();
}

/**
 * Print an isolated document by rendering pre-built HTML into a hidden iframe
 * and calling the iframe's own `print()`. This is the robust cross-device path:
 * the host page is never involved, so it cannot leak into the printout — unlike
 * the `triggerPrint` visibility trick, which mobile/tablet browsers don't honor
 * reliably (they print the whole app page). Callers pass the `<body>` inner HTML
 * (e.g. `renderToStaticMarkup(<PrescriptionPrintLayout … />)`).
 *
 * No-op on the server. The iframe is removed after `afterprint` (or a watchdog).
 * `onAfterPrint` (if provided) runs exactly once after the print completes — use
 * it to reset caller state (e.g. clear a pending-print request).
 */
export function printHtmlViaIframe(
  bodyHtml: string,
  title = 'Print',
  onAfterPrint?: () => void,
): void {
  if (typeof document === 'undefined') {
    onAfterPrint?.();
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  document.body.appendChild(iframe);

  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    // Defer removal so the print subsystem can finish closing its dialog.
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 200);
    onAfterPrint?.();
  };

  const doc = iframe.contentDocument;
  if (!doc) {
    cleanup();
    return;
  }
  doc.open();
  doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  @page { size: A4; margin: 0; }
  body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; }
  body > * { width: 210mm; min-height: 297mm; box-sizing: border-box; }
</style>
</head>
<body>${bodyHtml}</body>
</html>`);
  doc.close();

  const onLoad = () => {
    const w = iframe.contentWindow;
    if (!w) {
      cleanup();
      return;
    }
    const onAfter = () => {
      w.removeEventListener('afterprint', onAfter);
      cleanup();
    };
    w.addEventListener('afterprint', onAfter);
    try {
      w.focus();
      w.print();
    } catch {
      cleanup();
    }
    // Watchdog for browsers that don't fire afterprint.
    setTimeout(() => {
      try {
        w.removeEventListener('afterprint', onAfter);
      } catch {
        // ignore
      }
      cleanup();
    }, 60000);
  };

  if (iframe.contentDocument?.readyState === 'complete') {
    onLoad();
  } else {
    iframe.addEventListener('load', onLoad, { once: true });
    // Most browsers populate the iframe synchronously after doc.write/close.
    setTimeout(onLoad, 0);
  }
}

/**
 * Print stylesheet for the prescription print surface. Kept as a named export
 * for callers that still inject it via `<style>{PRINT_STYLES}</style>`.
 */
export const PRINT_STYLES = `${PAGE_STYLES}\n${makePrintStyles('prescription', 'pp-print-only')}`;

/**
 * Build the effective `PrescriptionPrintSettingsUI` from the saved org-default
 * row, falling back to `app_config` branding + sensible defaults when the
 * clinic hasn't run the wizard yet. The "Print Prescription" action and the
 * wizard both use this so a clinic that has only configured app_config can
 * still produce a usable printout.
 */
export function resolvePrintSettings(
  savedRow: PrescriptionPrintSettingsRow | null,
  appConfig: AppConfigRow | null,
): PrescriptionPrintSettingsUI {
  if (savedRow) return mapPrescriptionPrintSettingsToUI(savedRow);

  const defaultLogoSource: PrescriptionPrintLogoSource = orgLogoUrl
    ? 'standard'
    : orgLogoHorizontalUrl
      ? 'horizontal'
      : 'none';

  return {
    id: '',
    ...PRESCRIPTION_PRINT_DEFAULTS,
    logoSource: defaultLogoSource,
    clinicName: appConfig?.org_name ?? '',
    clinicAddress: appConfig?.org_address ?? '',
    clinicPhone: appConfig?.org_phone ?? '',
    clinicEmail: appConfig?.org_email ?? '',
  };
}

/**
 * Resolve the printing user's doctor identity from their linked provider.
 * Returns empty strings when no link is configured — the renderer hides the
 * doctor block entirely in that case.
 */
export function resolveDoctor(
  linkedProvider: AppointmentProviderRow | null,
  fallbackDisplayName?: string | null,
): Required<PrintDoctor> {
  const name =
    linkedProvider?.display_name?.trim() ||
    linkedProvider?.full_name?.trim() ||
    fallbackDisplayName?.trim() ||
    '';
  const credentials = linkedProvider?.specialty?.trim() ?? '';
  return { name, credentials };
}

/**
 * Compute a printable age string from a patient's birth_date (ISO date).
 * Returns '' when birth_date is missing or unparseable.
 */
export function formatPatientAge(birthDateIso: string | null | undefined): string {
  if (!birthDateIso) return '';
  const dob = new Date(birthDateIso);
  if (Number.isNaN(dob.getTime())) return '';
  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    now.getMonth() < dob.getMonth() ||
    (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) years--;
  if (years < 0) return '';
  return `${years} yrs`;
}
